<template>
  <div class="space-y-24">
    <header>
      <h2 class="body-2">
        {{ i18n(translations.heading2) }}
      </h2>

      <h1 class="heading-1">
        {{ i18n(translations.heading1) }}
      </h1>
    </header>

    <RevInfoBlock
      v-if="error"
      :cta="i18n(translations.tryAgain)"
      :icon="IconWarning"
      :title="i18n(translations.error)"
      variant="danger"
      @click="() => refresh()"
    />

    <div class="space-y-16">
      <DefectWarrantyCheckbox
        v-if="coverage"
        :backmarket-warranty="coverage.backmarketWarranty"
        :merchant-warranty="coverage.merchantWarranty"
      />

      <RevForm :initial-values @submit="withdraw">
        <template #default="{ values }">
          <div class="space-y-16">
            <PolicyCheckbox
              v-for="policy in withdrawablePolicies"
              :key="policy.id"
              v-model="values[policy.id]"
              :policy
            />

            <RevSkeleton
              v-if="status === 'pending'"
              height="3.625rem"
              shape="rectangle"
              width="100%"
            />

            <PolicyRefundPrice
              v-if="getSelectedPolicies(values).length > 0"
              :price="getRefundPrice(values)"
            />

            <RevButton
              :disabled="
                isSubmitting || getSelectedPolicies(values).length === 0
              "
              :loading="isSubmitting"
              type="submit"
              variant="primary"
            >
              {{ i18n(translations.confirm) }}
            </RevButton>
          </div>
        </template>
      </RevForm>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAsyncData, useRoute } from '#imports'
import { computed, ref } from 'vue'

import type { ApiSpecs } from '@backmarket/nuxt-module-http-v2/specs'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevForm } from '@ds/components/Form'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { RevSkeleton } from '@ds/components/Skeleton'
import { IconWarning } from '@ds/icons/IconWarning'

import translations from './PolicySelectionForm.translations'
import DefectWarrantyCheckbox from './components/DefectWarrantyCheckbox/DefectWarrantyCheckbox.vue'
import PolicyCheckbox from './components/PolicyCheckbox/PolicyCheckbox.vue'
import PolicyRefundPrice from './components/PolicyRefundPrice/PolicyRefundPrice.vue'

const i18n = useI18n()
const route = useRoute()
const logger = useLogger()
const { openErrorToast } = useTheToast()

type InsurancePolicy =
  ApiSpecs['/bm/orders/v1/orderline/:orderlineId/coverage']['GET']['response']['insurancePolicies'][number]

const $httpFetch = useHttpFetch()

const {
  data: coverage,
  error,
  status,
  refresh,
} = useAsyncData(async () => {
  return await $httpFetch('/bm/orders/v1/orderline/:orderlineId/coverage', {
    pathParams: {
      orderlineId: route.params.orderlineId.toString(),
    },
  })
})

const isSubmitting = ref(false)
const emit = defineEmits(['refunded'])

type FormValues = Record<number | string, boolean>

const withdrawablePolicies = computed(() => {
  if (!coverage.value) return []

  return coverage.value.insurancePolicies.filter((policy) => {
    return (
      policy.isWithdrawable &&
      policy.state !== 'TERMINATED' &&
      !policy.hasPendingTerminationRequest
    )
  })
})

const initialValues = computed<FormValues>(() => {
  if (!withdrawablePolicies.value) return {}

  const isSelected = withdrawablePolicies.value.length === 1

  return withdrawablePolicies.value.reduce(
    (acc, policy) => ({ ...acc, [policy.id]: isSelected }),
    {},
  )
})

function getSelectedPolicies(values: FormValues) {
  if (!withdrawablePolicies.value) return []

  return withdrawablePolicies.value.filter((policy) => values[policy.id])
}

function getRefundPrice(values: FormValues) {
  const selectedPolicies = getSelectedPolicies(values)

  const refundAmount = selectedPolicies.reduce(
    (totalAmount: number | null, policy: InsurancePolicy) => {
      if (policy.priceToRefund !== null) {
        if (totalAmount === null) {
          return parseFloat(policy.priceToRefund.amount)
        }

        return totalAmount + parseFloat(policy.priceToRefund.amount)
      }

      return totalAmount
    },
    null,
  )

  if (refundAmount === null) {
    return null
  }

  return {
    amount: refundAmount.toFixed(2),
    currency: selectedPolicies[0].price.currency,
  }
}

async function withdraw(values: FormValues) {
  isSubmitting.value = true

  const selectedPolicies = getSelectedPolicies(values)

  try {
    await Promise.all(
      selectedPolicies.map((policy: InsurancePolicy) =>
        $httpFetch('/bm/insurances/v1/policies/:policyId/termination', {
          method: 'POST',
          pathParams: { policyId: policy.id },
        }),
      ),
    )

    emit('refunded', getRefundPrice(values))
  } catch (e) {
    const err = e as Error
    err.cause = selectedPolicies

    logger.error('[INSURANCE][API][TERMINATION] Error terminating policies', {
      error: err,
      owners: ['bot-squad-b2cservices-front'],
    })
    openErrorToast()
  } finally {
    isSubmitting.value = false
  }
}
</script>
